import store from '@/store';

/**
 * Utility function to wait for profile data to be available in the store
 * This is useful when logging functions need client_player_id after registration/login
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 10000ms)
 * @param checkInterval - How often to check in milliseconds (default: 100ms)
 * @returns Promise that resolves when profile data is available or rejects on timeout
 */
export const waitForProfileData = (timeout: number = 10000, checkInterval: number = 100): Promise<any> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkProfileData = () => {
      const state = store.getState();
      const profileState = state.profile;

      // Check if we have the required profile data
      if (profileState?.client_player_id && profileState?.global_player_id) {
        resolve(profileState);
        return;
      }

      // Check if we've exceeded the timeout
      if (Date.now() - startTime >= timeout) {
        console.warn('waitForProfileData: Timeout waiting for profile data');
        reject(new Error('Timeout waiting for profile data'));
        return;
      }

      // Continue checking
      setTimeout(checkProfileData, checkInterval);
    };

    // Start checking immediately
    checkProfileData();
  });
};

/**
 * Enhanced logging function that waits for profile data before executing
 * 
 * @param logFunction - The logging function to execute
 * @param args - Arguments to pass to the logging function
 * @param timeout - Maximum time to wait for profile data
 */
export const logWithProfileData = async (
  logFunction: (...args: any[]) => void,
  args: any[] = [],
  timeout: number = 10000
): Promise<void> => {
  try {
    await waitForProfileData(timeout);
    logFunction(...args);
  } catch (error) {
    console.error('Failed to log with profile data:', error);
    // Still attempt to log even if profile data is not available
    logFunction(...args);
  }
};